// src/pages/DecisionPage.jsx
// Owner clicks Accept/Reject link from WhatsApp → lands here
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

export default function DecisionPage() {
  const { token } = useParams(); // token = applicationId:decision (accept/reject)
  const [state, setState] = useState("loading"); // loading | confirm | done | error
  const [app, setApp] = useState(null);
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    // token format: "appId_accept" or "appId_reject"
    const parts = token.split("_");
    const action = parts.pop();
    const appId = parts.join("_");
    setDecision(action);

    getDoc(doc(db, "applications", appId)).then(snap => {
      if (!snap.exists()) { setState("error"); return; }
      const data = { id: snap.id, ...snap.data() };
      if (data.status !== "pending") { setState("done"); setApp(data); return; }
      setApp(data);
      setState("confirm");
    }).catch(() => setState("error"));
  }, [token]);

  async function confirm() {
    setState("processing");
    const parts = token.split("_");
    parts.pop();
    const appId = parts.join("_");

    try {
      await updateDoc(doc(db, "applications", appId), {
        status: decision === "accept" ? "accepted" : "rejected",
        decidedAt: serverTimestamp()
      });

      if (decision === "accept") {
        // Mark cat as pending (will be marked adopted when confirmed)
        await updateDoc(doc(db, "cats", app.catId), { status: "pending" });

        // Trigger WhatsApp intro message to adopter via backend
        await fetch("/api/send-intro", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            adopterPhone: app.adopter?.phone,
            ownerPhone: app.ownerPhone,
            catName: app.catName,
            ownerName: app.ownerName || "the owner",
          })
        });
      } else {
        // Notify adopter of rejection
        await fetch("/api/notify-rejected", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            adopterPhone: app.adopter?.phone,
            catName: app.catName
          })
        });
      }

      setState("done");
    } catch (err) {
      console.error(err);
      setState("error");
    }
  }

  if (state === "loading" || state === "processing") return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div className="spinner" style={{width:40,height:40,margin:"0 auto"}}/>
        <p style={{marginTop:16,color:"var(--muted)"}}>
          {state === "processing" ? "Processing your decision…" : "Loading…"}
        </p>
      </div>
    </div>
  );

  if (state === "error") return (
    <div style={styles.page}>
      <div style={styles.card}>
        <p style={{fontSize:48}}>❌</p>
        <h2 style={styles.title}>Link Expired or Invalid</h2>
        <p style={styles.sub}>This link may have already been used or is invalid. Please check your WhatsApp for the latest notification.</p>
      </div>
    </div>
  );

  if (state === "done") return (
    <div style={styles.page}>
      <div style={styles.card}>
        <p style={{fontSize:56}}>{decision === "accept" ? "🎉" : "💙"}</p>
        <h2 style={styles.title}>
          {decision === "accept"
            ? `You've accepted the adoption request!`
            : `You've declined the request`}
        </h2>
        <p style={styles.sub}>
          {decision === "accept"
            ? `Great news! We've sent an intro WhatsApp message to ${app?.adopter?.name}. They'll reach out to you shortly to arrange everything. Once the adoption is confirmed, don't forget to mark ${app?.catName} as adopted on your dashboard.`
            : `We've let the applicant know. ${app?.catName} is still listed as available for other potential adopters.`}
        </p>
        {decision === "accept" && (
          <div style={styles.infoBox}>
            <p style={styles.infoTitle}>📱 What happens next:</p>
            <ol style={styles.infoList}>
              <li>The adopter will WhatsApp you directly</li>
              <li>Arrange a meet-and-greet at your convenience</li>
              <li>Once confirmed, mark {app?.catName} as "Adopted" on your dashboard</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );

  // Confirm state
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <p style={{fontSize:48}}>{decision === "accept" ? "🐾" : "💙"}</p>
        <h2 style={styles.title}>
          {decision === "accept"
            ? `Accept adoption request?`
            : `Decline this request?`}
        </h2>

        {app && (
          <div style={styles.appSummary}>
            <p style={styles.summaryTitle}>Application for <strong>{app.catName}</strong></p>
            <div style={styles.summaryGrid}>
              <SummaryRow label="Name" value={app.adopter?.name}/>
              <SummaryRow label="Living" value={app.adopter?.living}/>
              <SummaryRow label="Experience" value={app.adopter?.experience}/>
              <SummaryRow label="Household" value={app.adopter?.household}/>
              <SummaryRow label="Reason" value={app.adopter?.reason}/>
              <SummaryRow label="Other pets" value={app.adopter?.otherPets}/>
            </div>
          </div>
        )}

        <div style={styles.btnRow}>
          {decision === "accept" ? (
            <>
              <button onClick={confirm} className="btn-primary" style={{flex:1,justifyContent:"center",padding:14}}>
                ✅ Yes, Accept & Connect on WhatsApp
              </button>
            </>
          ) : (
            <>
              <button onClick={confirm} style={{...styles.rejectBtn, flex:1}}>
                Confirm Decline
              </button>
            </>
          )}
        </div>
        <p style={{fontSize:13,color:"var(--muted)",marginTop:12}}>
          {decision === "accept"
            ? "The adopter will receive your WhatsApp number and be introduced as the owner of " + app?.catName + "."
            : "The adopter will be notified politely that the request was unsuccessful."}
        </p>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{display:"flex",gap:8,fontSize:14,padding:"6px 0",borderBottom:"1px solid var(--warm)"}}>
      <span style={{fontWeight:600,color:"var(--mid)",minWidth:90}}>{label}:</span>
      <span style={{color:"var(--text)"}}>{value}</span>
    </div>
  );
}

const styles = {
  page: { minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 },
  card: { background:"#fff", borderRadius:24, padding:"48px 36px", width:"100%", maxWidth:500, boxShadow:"var(--shadow-l)", textAlign:"center" },
  title: { fontFamily:"'Playfair Display',serif", fontSize:26, color:"var(--deep)", margin:"16px 0 10px" },
  sub: { color:"var(--muted)", fontSize:15, lineHeight:1.7 },
  appSummary: { background:"var(--warm)", borderRadius:14, padding:20, margin:"20px 0", textAlign:"left" },
  summaryTitle: { fontSize:15, marginBottom:12, color:"var(--deep)" },
  summaryGrid: { display:"flex", flexDirection:"column" },
  btnRow: { display:"flex", gap:12, marginTop:24 },
  rejectBtn: { background:"var(--danger)", color:"#fff", border:"none", padding:14, borderRadius:50, fontSize:15, cursor:"pointer", fontFamily:"'Outfit',sans-serif", fontWeight:600 },
  infoBox: { background:"var(--warm)", borderRadius:14, padding:20, marginTop:20, textAlign:"left" },
  infoTitle: { fontWeight:600, marginBottom:10, color:"var(--deep)" },
  infoList: { paddingLeft:20, color:"var(--text)", fontSize:15, lineHeight:2 },
};
